import React, { useEffect, useState, useCallback } from "react";
import { Space, message, Button, Typography, Popconfirm, Alert } from "antd";
import Util from "../../Util";

const moment = require("moment-timezone");
const TIMEZONE = "Asia/Bangkok";

const { Title, Text } = Typography;

const errorText = (result) =>
  Array.isArray(result.msg)
    ? result.msg[Util.getLang()]
    : result.msg || "An error occurred";

function PaymentReset() {
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [status, setStatus] = useState(null);

  const loadStatus = useCallback(async () => {
    const result = await Util.sendGet(
      "/api/moresheetStaff/getHytextsResetStatus",
      {}
    );
    if (result.success) setStatus(result.data);
    else messageApi.error(errorText(result));
  }, [messageApi]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const resetFailedPayments = async () => {
    setLoading(true);
    setResetResult(null);
    try {
      const result = await Util.sendPost(
        "/api/moresheetStaff/setRetryHytextsFailedPayments",
        {}
      );
      if (result.success) {
        messageApi.success("Successfully reset failed payments.");
        setResetResult(result);
      } else {
        messageApi.error(`Failed to reset payments: ${errorText(result)}`);
      }
    } catch (error) {
      messageApi.error(
        "An unexpected error occurred while resetting payments."
      );
    } finally {
      setLoading(false);
      loadStatus();
    }
  };

  const undoLastReset = async () => {
    setLoading(true);
    setResetResult(null);
    try {
      const result = await Util.sendPost(
        "/api/moresheetStaff/undoRetryHytextsFailedPayments",
        {}
      );
      if (result.success) {
        messageApi.success(
          `ย้อนการ Reset สำเร็จ: จ่ายไม่สำเร็จ ${result.pausedPayments.modifiedCount} รายการ, ทบยอด ${result.carriedPayments.modifiedCount} รายการ`
        );
      } else {
        messageApi.error(`ย้อนการ Reset ไม่สำเร็จ: ${errorText(result)}`);
      }
    } catch (error) {
      messageApi.error("An unexpected error occurred while undoing reset.");
    } finally {
      setLoading(false);
      loadStatus();
    }
  };

  const stalePending = status && status.stalePendingCount > 0;
  const latestMonth =
    status && status.latestMonth
      ? `${status.latestMonth.year}-${String(status.latestMonth.month).padStart(2, "0")}`
      : "";
  const lastReset = status && status.lastReset;

  return (
    <Space direction="vertical" size={12}>
      {contextHolder}
      <Title level={4}>
        นำรายการที่จ่ายไม่สำเร็จ/ทบยอดกลับมาทำจ่ายในรอบปัจจุบัน(เฉพาะส่วนของ
        Hytexts)
      </Title>
      {stalePending && (
        <Alert
          type="warning"
          showIcon
          message={`ยังมีรายได้ก่อนเดือน ${latestMonth} ที่รอทำจ่ายอยู่ ${status.stalePendingCount} รายการ`}
          description="รอบทำจ่ายก่อนหน้าอาจยังไม่ได้ทำ '6. อัพเดตสถานะทำจ่ายสำเร็จ' ควรทำข้อ 6 ให้เสร็จก่อน Reset ไม่อย่างนั้นยอดเก่าจะปนกับยอดของรอบที่รออัพเดต"
        />
      )}
      <Popconfirm
        title="ยืนยันการ Reset"
        description={
          stalePending
            ? "รอบก่อนอาจยังไม่ได้ทำข้อ 6 คุณแน่ใจหรือไม่ว่าต้องการ Reset ตอนนี้"
            : "คุณแน่ใจหรือไม่ว่าต้องการนำรายการที่จ่ายไม่สำเร็จและรายการทบยอดทั้งหมดกลับมาทำจ่ายในรอบปัจจุบัน"
        }
        onConfirm={resetFailedPayments}
        okText="ยืนยัน"
        cancelText="ยกเลิก"
        okButtonProps={{ danger: stalePending }}
      >
        <Button type="primary" loading={loading}>
          Reset Failed Payments
        </Button>
      </Popconfirm>
      {resetResult && (
        <Text>
          รายการที่จ่ายไม่สำเร็จ: {resetResult.pausedPayments.modifiedCount}
        </Text>
      )}
      {resetResult && (
        <Text>
          รายการที่ทบยอดไว้: {resetResult.carriedPayments.modifiedCount}
        </Text>
      )}
      {lastReset && (
        <Space direction="vertical" size={4}>
          <Text type="secondary">
            Reset ล่าสุด:{" "}
            {moment(lastReset.created).tz(TIMEZONE).format("DD/MM/YYYY HH:mm")}{" "}
            (จ่ายไม่สำเร็จ {lastReset.pausedCount} รายการ, ทบยอด{" "}
            {lastReset.carriedCount} รายการ)
            {lastReset.undone &&
              ` — ย้อนแล้วเมื่อ ${moment(lastReset.undoneAt)
                .tz(TIMEZONE)
                .format("DD/MM/YYYY HH:mm")}`}
          </Text>
          {!lastReset.undone && (
            <Popconfirm
              title="ยืนยันการย้อน Reset ล่าสุด"
              description="รายการจาก Reset ล่าสุดที่ยังรอทำจ่ายจะกลับเป็นสถานะเดิม (จ่ายไม่สำเร็จ/ทบยอด) รายการที่ถูกปรับในขั้นตอนอื่นไปแล้วจะไม่ถูกแก้"
              onConfirm={undoLastReset}
              okText="ยืนยัน"
              cancelText="ยกเลิก"
            >
              <Button danger loading={loading}>
                Undo Reset ล่าสุด
              </Button>
            </Popconfirm>
          )}
        </Space>
      )}
    </Space>
  );
}

export default PaymentReset;
